import React, { useLayoutEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { Box } from "@mui/system";
import { Divider } from "@mui/material";
import { StyledFormLabels, StyledInputGrid } from "customStyled";
import FormOtpInput from "./FormOtpInput";
import { markTouched } from "../rhf/useAppForm";
import { useTranslation } from "react-i18next";

// OTP boxes bound to the `keyName` array of the form
const OtpFieldReact = ({ form, keyName = "otp", ...rest }) => {
  const otp = useWatch({ control: form.control, name: keyName }) || [];

  // The boxes count as touched from the start, so a box shows its error as
  // soon as the code is checked (like before)
  useLayoutEffect(() => {
    markTouched(form, keyName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OtpBoxes
      {...rest}
      keyName={keyName}
      count={otp.length}
      setDigit={(index, char) =>
        form.setValue(`${keyName}.${index}`, char, { shouldValidate: true })
      }
    />
  );
};

const OtpBoxes = ({ label, length, keyName, count, setDigit: setDigitAt }) => {
  const { t } = useTranslation("form");
  // ✅ Create refs dynamically
  const refs = useRef([]);

  const setDigit = (index, char) => {
    if (index < count) setDigitAt(index, char);
  };

  const handlePasteOtp = (e, startId) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    const digits = pasted.replace(/\D/g, "").split("");
    if (digits.length === 0) return;

    const match = startId.match(/(\d+)/);
    const startIndex = match ? Number(match[1]) : 0;

    digits.forEach((char, i) => setDigit(startIndex + i, char));

    // focus last filled box
    const last = Math.min(startIndex + digits.length - 1, length - 1);
    refs.current[last]?.focus();
  };
  return (
    <StyledInputGrid>
      <StyledFormLabels htmlFor="email">{label} {t("secureCode")}</StyledFormLabels>
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          marginTop: "5px",
          alignItems: "center",
        }}
      >
        {Array.from({ length: count }, (_, i) => (
          <React.Fragment key={i}>
            {i !== 0 && i % 3 === 0 ? (
              <Divider
                flexItem
                sx={{
                  width: "18px",
                  height: "0px",
                  borderWidth: "3px",
                  alignSelf: "center",
                }}
              />
            ) : null}
            <FormOtpInput
              id={`${keyName}.${i}`}
              ref={(el) => (refs.current[i] = el)} // populate refs.current array
              prevRef={{ current: refs.current[i - 1] }}
              nextRef={{ current: refs.current[i + 1] }}
              onPasteOtp={handlePasteOtp}
            />
          </React.Fragment>
        ))}
      </Box>
    </StyledInputGrid>
  );
};

export default OtpFieldReact;
